import { ChartFrequency, IComposableChartsWidget } from 'wisestep-api';

import { generalTimePeriodList } from '../../../../analyticsPage/helper/timePeriodList';
import MappingBarChartPostgre from '../../../BaseCharts/mappings/new/MappingBarChartPostgre';
import MappingsMetricPostgre from '../../../BaseCharts/mappings/new/MappingsMetricPostgre';
import MappingsTablePostgre from '../../../BaseCharts/mappings/new/MappingsTablePostgre';
import MappingStackedBarChartPostgre from '../../../BaseCharts/mappings/new/MappingStackedBarChartPostgre';
import { ChartScope } from '../../../types';
import { MappingsWidget, getChartFreqForTimePeriod } from '../../helper';
import { newCandidateWidgetsByTimeAndScope } from '../../newCandidate/helper/postgresNewCandidateDashboardKeys';

const getChartWidgetByTimeAndScope = (
  timePeriodId: string,
  scope: ChartScope,
  frequency: ChartFrequency = getChartFreqForTimePeriod[timePeriodId]
): Partial<Record<MappingsWidget, IComposableChartsWidget>> => {
  const defaultQuery = {
    category: 'mappings',
    filters: [],
    type: 'MAPPING_COUNT',
    frequency,
    range: timePeriodId,
    scope,
    candidateStatusTags: [],
  };
  const freqInRegularCase = frequency.charAt(0).toUpperCase() + frequency.slice(1).toLowerCase();

  const widgetList = {
    [MappingsWidget.MAPPING_COUNT]: {
      name: 'All Mappings created or updated',
      filters: {},
      type: `metric_all_mappings_${timePeriodId}_${scope}_postgre`,
      category: 'mappings',
      component: (
        <MappingsMetricPostgre
          query={{
            ...defaultQuery,
            type: 'MAPPING_COUNT',
            candidateStatusTags: ['mapped', 'uploaded', 'applied'],
          }}
          title="All Mappings created or updated"
          widgetName={MappingsWidget.MAPPING_COUNT}
        />
      ),
    },
    [MappingsWidget.MAPPED_STATUS_COUNT]: {
      name: 'Mappings created or updated',
      filters: {},
      type: `metric_mappings_${timePeriodId}_${scope}_postgre`,
      category: 'mappings',
      component: (
        <MappingsMetricPostgre
          query={{
            ...defaultQuery,
            type: 'MAPPING_COUNT',
            candidateStatusTags: ['mapped'],
          }}
          title="Mappings created or updated"
          widgetName={MappingsWidget.MAPPED_STATUS_COUNT}
        />
      ),
    },
    [MappingsWidget.CLIENTS]: {
      name: 'Total Mappings created or updated to Clients',
      filters: {},
      type: `table_clients_mappings_${timePeriodId}_${scope}_postgre`,
      category: 'mappings',
      component: (
        <MappingsTablePostgre
          query={{ ...defaultQuery, type: 'CLIENTS', candidateStatusTags: ['mapped'] }}
          title="Total Mappings created or updated to Clients"
          widgetName={MappingsWidget.CLIENTS}
        />
      ),
    },
    [MappingsWidget.RECRUITER_NAME]: {
      name: 'Total Mappings created or updated By Users',
      filters: {},
      type: `table_recruiter_mappings_${timePeriodId}_${scope}_postgre`,
      category: 'mappings',
      component: (
        <MappingsTablePostgre
          query={{ ...defaultQuery, type: 'RECRUITER_NAME', candidateStatusTags: ['mapped'] }}
          title="Total Mappings created or updated By Users"
          widgetName={MappingsWidget.RECRUITER_NAME}
        />
      ),
    },
    [MappingsWidget.ACCOUNT_MANAGER]: {
      name: 'Total Mappings created or updated to Account Managers',
      filters: {},
      type: `table_am_mappings_${timePeriodId}_${scope}_postgre`,
      category: 'mappings',
      component: (
        <MappingsTablePostgre
          query={{ ...defaultQuery, type: 'ACCOUNT_MANAGER', candidateStatusTags: ['mapped'] }}
          title="Total Mappings created or updated to Account Managers"
          widgetName={MappingsWidget.ACCOUNT_MANAGER}
        />
      ),
    },
    [MappingsWidget.JOB_TYPE]: {
      name: 'Total Mappings created or updated by Job Type',
      filters: {},
      type: `table_job_type_mappings_${timePeriodId}_${scope}_postgre`,
      category: 'mappings',
      component: (
        <MappingsTablePostgre
          query={{ ...defaultQuery, type: 'JOB_TYPE', candidateStatusTags: ['mapped'] }}
          title="Total Mappings created or updated by Job Type"
          widgetName={MappingsWidget.JOB_TYPE}
        />
      ),
    },
    [MappingsWidget.SOURCE]: {
      name: 'Total Mappings created or updated by Source',
      filters: {},
      type: `table_source_mappings_${timePeriodId}_${scope}_postgre`,
      category: 'mappings',
      component: (
        <MappingsTablePostgre
          query={{ ...defaultQuery, type: 'SOURCE', candidateStatusTags: ['mapped'] }}
          title="Total Mappings created or updated by Source"
          widgetName={MappingsWidget.SOURCE}
        />
      ),
    },
    [MappingsWidget.DOWNLOAD_SOURCE]: {
      name: 'Total Mappings created or updated by Download Source',
      filters: {},
      type: `table_download_source_mappings_${timePeriodId}_${scope}_postgre`,
      category: 'mappings',
      component: (
        <MappingsTablePostgre
          query={{ ...defaultQuery, type: 'DOWNLOAD_SOURCE', candidateStatusTags: ['mapped'] }}
          title="Total Mappings created or updated by Download Source"
          widgetName={MappingsWidget.DOWNLOAD_SOURCE}
        />
      ),
    },
    [MappingsWidget.PROFILE_DOWNLOAD_SOURCE]: {
      name: 'Profile Download Source - Uploaded',
      filters: {},
      type: `table_profile_download_source_mappings_${timePeriodId}_${scope}_postgre`,
      category: 'mappings',
      component: (
        <MappingsTablePostgre
          query={{ ...defaultQuery, type: 'DOWNLOAD_SOURCE', candidateStatusTags: ['uploaded'] }}
          title="Profile Download Source - Uploaded"
          widgetName={MappingsWidget.PROFILE_DOWNLOAD_SOURCE}
        />
      ),
    },
    [MappingsWidget.RECRUITER_TEAM]: {
      name: 'Mappings by Users with Teams',
      filters: {},
      type: `table_recruiter_team_mappings_${timePeriodId}_${scope}_postgre`,
      category: 'mappings',
      component: (
        <MappingsTablePostgre
          query={{ ...defaultQuery, type: 'RECRUITER_TEAM', candidateStatusTags: ['mapped'] }}
          title="Mappings by Users with Teams"
          widgetName={MappingsWidget.RECRUITER_TEAM}
        />
      ),
    },
    [MappingsWidget.JOB_TITLE]: {
      name: 'Mappings By Job Title',
      filters: {},
      type: `table_job_title_mappings_${timePeriodId}_${scope}_postgre`,
      category: 'mappings',
      component: (
        <MappingsTablePostgre
          query={{ ...defaultQuery, type: 'JOB_TITLE', candidateStatusTags: ['mapped'] }}
          title="Mappings By Job Title"
          widgetName={MappingsWidget.JOB_TITLE}
        />
      ),
    },
    [MappingsWidget.RECRUITER_TEAM_JOBTYPE]: {
      name: 'Recuiter Mappings by Job Type',
      filters: {},
      type: `table_recruiter_team_jobtype_mappings_${timePeriodId}_${scope}_postgre`,
      category: 'mappings',
      component: (
        <MappingsTablePostgre
          query={{
            ...defaultQuery,
            type: 'RECRUITER_TEAM_JOB_TYPE',
            candidateStatusTags: ['mapped'],
          }}
          title="Recuiter Mappings by Job Type"
          widgetName={MappingsWidget.RECRUITER_TEAM_JOBTYPE}
        />
      ),
    },
    [MappingsWidget.RECRUITER_DOWNLOAD_SOURCE]: {
      name: 'Download Source for profiles by mapped candidates',
      filters: {},
      type: `table_recruiter_download_source_mappings_${timePeriodId}_${scope}_postgre`,
      category: 'mappings',
      component: (
        <MappingsTablePostgre
          query={{
            ...defaultQuery,
            type: 'RECRUITER_DOWNLOAD_SOURCE',
            candidateStatusTags: ['mapped'],
          }}
          title="Download Source for profiles by mapped candidates"
          widgetName={MappingsWidget.RECRUITER_DOWNLOAD_SOURCE}
        />
      ),
    },
    [MappingsWidget.RECRUITER_SOURCE]: {
      name: 'Source for profiles by mapped candidates',
      filters: {},
      type: `table_recruiter_source_mappings_${timePeriodId}_${scope}_postgre`,
      category: 'mappings',
      component: (
        <MappingsTablePostgre
          query={{ ...defaultQuery, type: 'RECRUITER_SOURCE', candidateStatusTags: ['mapped'] }}
          title="Source for profiles by mapped candidates"
          widgetName={MappingsWidget.RECRUITER_SOURCE}
        />
      ),
    },
    [MappingsWidget.RECRUITER_DOWNLOAD_SOURCE_UPLOADED]: {
      name: 'Download Source for profiles by uploaded candidates',
      filters: {},
      type: `table_recruiter_download_source_uploaded_mappings_${timePeriodId}_${scope}_postgre`,
      category: 'mappings',
      component: (
        <MappingsTablePostgre
          query={{
            ...defaultQuery,
            type: 'RECRUITER_DOWNLOAD_SOURCE',
            candidateStatusTags: ['uploaded'],
          }}
          title="Download Source for profiles by uploaded candidates"
          widgetName={MappingsWidget.RECRUITER_DOWNLOAD_SOURCE}
        />
      ),
    },
    [MappingsWidget.RECRUITER_SOURCE_UPLOADED]: {
      name: 'Source for profiles by uploaded candidates',
      filters: {},
      type: `table_recruiter_source_uploaded_mappings_${timePeriodId}_${scope}_postgre`,
      category: 'mappings',
      component: (
        <MappingsTablePostgre
          query={{ ...defaultQuery, type: 'RECRUITER_SOURCE', candidateStatusTags: ['uploaded'] }}
          title="Source for profiles by uploaded candidates"
          widgetName={MappingsWidget.RECRUITER_SOURCE}
        />
      ),
    },
    [MappingsWidget.BAR_CHART_TREND]: {
      name: `Total Mappings created or updated - ${freqInRegularCase}`,
      filters: {},
      type: `barchart_mappings_${timePeriodId}_${scope}_${frequency}_postgre`,
      category: 'mappings',
      component: (
        <MappingBarChartPostgre
          query={{ ...defaultQuery, type: 'MAPPING_TREND', candidateStatusTags: ['mapped'] }}
          title={`Total Mappings created or updated - ${freqInRegularCase}`}
          widgetName={MappingsWidget.BAR_CHART_TREND}
        />
      ),
    },
    [MappingsWidget.STACKEDBAR_CLIENTS]: {
      name: `Total Mappings created or updated to Clients - ${freqInRegularCase}`,
      filters: {},
      type: `stackedbarchart_mappings_client_${timePeriodId}_${scope}_${frequency}_postgre`,
      category: 'mappings',
      component: (
        <MappingStackedBarChartPostgre
          query={{
            ...defaultQuery,
            type: 'MAPPING_TREND_CLIENTS',
            candidateStatusTags: ['mapped'],
          }}
          title={`Total Mappings created or updated to Clients - ${freqInRegularCase}`}
          widgetName={MappingsWidget.STACKEDBAR_CLIENTS}
        />
      ),
    },
    [MappingsWidget.TABLE_TIME_TO_SERVICE]: {
      name: 'Time taken to map candidates',
      filters: {},
      type: `table_mappings_service_${timePeriodId}_${scope}_${frequency}_postgre`,
      category: 'mappings',
      component: (
        <MappingsTablePostgre
          query={{
            ...defaultQuery,
            type: 'TABLE_TIME_TO_SERVICE',
            candidateStatusTags: ['mapped'],
          }}
          title="Time taken to map candidates"
          widgetName={MappingsWidget.TABLE_TIME_TO_SERVICE}
        />
      ),
    },
  };

  return widgetList;
};

export const postgreMappingsDashboardKeys = (
  timePeriodId: string,
  scope: ChartScope
): IComposableChartsWidget[] => {
  const defaultWidgetList = Object.values(getChartWidgetByTimeAndScope(timePeriodId, scope));
  const extraWidgetList = {};
  const newCandidateWidgetList =
    scope !== ChartScope.TEAMS
      ? Object.values(newCandidateWidgetsByTimeAndScope(timePeriodId, scope))
      : [];
  return [
    ...defaultWidgetList,
    ...newCandidateWidgetList,
    ...(extraWidgetList[timePeriodId] ?? []),
  ];
};

export const postgreMappingsDashboardKeysObjectList = generalTimePeriodList.flatMap(
  (timePeriod) => [
    ...postgreMappingsDashboardKeys(timePeriod.id, ChartScope.WORKSPACE),
    ...postgreMappingsDashboardKeys(timePeriod.id, ChartScope.TEAMS),
    ...postgreMappingsDashboardKeys(timePeriod.id, ChartScope.LOGGED_IN_USER),
  ]
);
